/**
 * 帖子发布和草稿
 */
import request, { MethodEnum, serverTriggerUrl } from '@/utils/fetch';
import { IDevice } from '@/api/device';
import type { IArticalAuther } from '@/api/thread/list'
import { thread_reg } from '@/utils/reg';
import { oldThreadPhotoPrefix } from '@/constants/url';
import { getRandomKey } from '@/utils';
import { ICommentListRes } from '@/api/detail';

export enum THREAD_APIS {
  GET_THREAD_DETAIL = '/forum/thread/',
  GET_THREAD_DRAFT_DETAIL = '/forum/thread/drafts/',
  CREATE_THREAD = '/forum/thread',
  CREATE_THREAD_DRAFT = '/forum/thread/draft',
  UPDATE_THREAD = '/forum/thread/',
  UPDATE_THREAD_DRAFT = '/forum/thread/drafts/',
  GET_THREAD_REPLAY = '/forum/thread/{tid}/reply',
  POST_THREAD_REPLY = '/forum/thread/{tid}/reply',
  GET_THREAD_HOT = '/forum/thread/hot',
}

export enum ContentTypeEnum {
  text = 1,
  photo = 2,
  iframeLink = 3,
  video = 4,
  customLink = 5,
}

/** 请求 interface */
export interface IUpdateThreadReq {
  tid: string | number;
}

/** 响应 interface */
export interface IGetThreadRes {
  item: {
    tid: number;
    fid: number;
    posttableid: number;
    typeid: number;
    subject: string;
    devices: IDevice[];
    message: string;
    special: number;
  };
}

export interface ICreateThreadRes {
  item: number;
  ext: {
    tips: {
      type: string;
      value: number;
      message: string;
    }
  };
}

export interface IUpdateThreadRes {
  item: number;
}

/** 回帖数据 */
export interface IReplyItem {
  anonymous: number;
  author?: Omit<IArticalAuther, 'rank'> & {
    rank: NonNullable<IArticalAuther['rank']>;
  };
  comment: number;
  dateline: number;
  fid: number;
  is_stick: number;
  message: string;
  pid: number;
  platform: number;
  position: number;
  tid: number;
}

export interface IReplyItemEncode extends IReplyItem {
  /** 表情包编码后的内容 */
  context: string;
}

export const handleResourceUrl = (message: []) => {
  message.forEach((item: any, index: number, messageArr: any) => {
    if (item.type === ContentTypeEnum.photo) {
      // 兼容老帖子 OR 本地图片
      const isOldImgSrc = item.imgsrc && (!item.imgsrc.startsWith('https://') || item.imgsrc.includes('localhost'));
      const isOldImg = item.url && (!item.url.startsWith('https://') || item.url.includes('localhost'));

      item.imgsrc = isOldImgSrc ? `${oldThreadPhotoPrefix}/${item.imgsrc}` : (item.imgsrc || '');
      item.url = isOldImg ? `${oldThreadPhotoPrefix}/${item.url}` : (item.url || '');

      // 判断是否为gif图片，如果是gif则不处理multi_size
      const isGif = item.url && (item.url.toLowerCase().includes('.gif') || item.url.toLowerCase().includes('gif'));

      if (!isGif) {
        // 非gif图片才处理multi_size
        if (item?.multi_size?.small) {
          item.multi_size.small = isOldImg ? `${oldThreadPhotoPrefix}/${item.multi_size.small}` : (item.multi_size.small || '');
        }
        if (item?.multi_size?.large) {
          item.multi_size.large = isOldImg ? `${oldThreadPhotoPrefix}/${item.multi_size.large}` : (item.multi_size.large || '');
        }
        if (item?.multi_size?.extra_large) {
          item.multi_size.extra_large = isOldImg ? `${oldThreadPhotoPrefix}/${item.multi_size.extra_large}` : (item.multi_size.extra_large || '');
        }
        if (item?.multi_size?.medium) {
          item.multi_size.medium = isOldImg ? `${oldThreadPhotoPrefix}/${item.multi_size.medium}` : (item.multi_size.medium || '');
        }
      }
      if (isGif) {
        item.multi_size = null;
      }
    } else if (item.type === ContentTypeEnum.video) {
      const isOldImgSrc = item.imgsrc && (!item.imgsrc.startsWith('https://') || item.imgsrc.includes('localhost'));
      item.imgsrc = isOldImgSrc ? `${oldThreadPhotoPrefix}/${item.imgsrc}` : (item.imgsrc || '');
    }
  });

  return message;
};

// '[i=ss] 本帖最后由 djiuser_M3VxmMtGrlwg 于 2023-11-21 12:28 编辑 [/i]\n' +
//     '\n' +
//     '[{"nameindex":"_1_1","type":1,"url":"","desc":"fe fa s d","imgsrc":"static\\/image\\/text.jpg"},{"nameindex":"_3_1","type":3,"url":"https:\\/\\/vip.aasky.net\\/video_play\\/e1116d43-74a6-4ba4-aac8-4cf65970b476","desc":"","imgsrc":"static\\/image\\/video.jpg"}]'

export const handleThreadMessage = (data: IGetThreadRes) => {
  let message: any = data.item.message;
  const hasEditMsg = thread_reg.test(message);
  const messageMatch = message.match(thread_reg);
  if (hasEditMsg && messageMatch?.length) {
    message = message.replace(messageMatch[0], '').trim();
  }
  const editorInitialValue = (key: string) => ({ key, type: ContentTypeEnum.text, desc: '' });

  if (typeof message == "string") {
    message = JSON.parse(message.replace(/\n/g, "\\n").replace(/\r/g, "\\r").replace(/\t/g, "\\t"));
    // if (data.item.special === 6) {
    //   message = handleResourceUrl(message);
    // }

    message = handleResourceUrl(message);

    message.map((messageItem: any) => {
      if (messageItem.type === ContentTypeEnum.video || messageItem.type === ContentTypeEnum.photo) {
        messageItem.percent = 1;
        messageItem.isCompleted = true;
      }
    })

    const messageCopy = message.concat();

    message = [];
    // 保证新旧帖子的每个模块之间有文本输入框
    messageCopy.forEach((item: any, index: number, messageArr: any) => {
      if (item.type !== ContentTypeEnum.text && messageArr[index - 1]?.type !== ContentTypeEnum.text) {
        message.push(editorInitialValue(getRandomKey()));
      }
      item.key = getRandomKey();
      message.push(item);
      if (messageArr[messageArr.length - 1]?.type !== ContentTypeEnum.text && index === messageArr.length - 1) {
        message.push(editorInitialValue(getRandomKey()));
      }
    });

  }

  data.item.message = message;
  return data;
}

/** API */
export const getThreadDetail = (isServerTrigger = false, tid: string | number, headers: any = {}, device: string = 'desktop') => {
  const url = `${isServerTrigger ? serverTriggerUrl : ''}${THREAD_APIS.GET_THREAD_DETAIL}${tid}`;
  const key = 'GetThreadDetail';
  return request({ isServerTrigger, url, key, headers, params: { device } }).then((data: IGetThreadRes) => {
    return handleThreadMessage(data);
  }).catch(() => { });
}

export const getThreadDraftDetail = (isServerTrigger = false, tid: string | number, headers: any = {}, device: string = 'desktop') => {
  const url = `${isServerTrigger ? serverTriggerUrl : ''}${THREAD_APIS.GET_THREAD_DRAFT_DETAIL}${tid}`;
  const key = 'GetThreadDraftDetail';
  return request({ isServerTrigger, url, key, headers, params: { device } }).then((data: IGetThreadRes) => {
    return handleThreadMessage(data);
  }).catch(() => { });
}

export const createThread = (params = {}) => {
  return request({
    url: THREAD_APIS.CREATE_THREAD,
    method: MethodEnum.POST,
    params,
  }).then((data: ICreateThreadRes) => data);
};

export const createThreadDraft = (params = {}) => {
  return request({
    url: THREAD_APIS.CREATE_THREAD_DRAFT,
    method: MethodEnum.POST,
    params,
  }).then((data: ICreateThreadRes) => data);
}

export const updateThread = (params: any = {}) => {
  return request({
    url: `${THREAD_APIS.UPDATE_THREAD}${params.tid}`,
    method: MethodEnum.PUT,
    params,
  }).then((data: IUpdateThreadRes) => data);
};

export const updateThreadDraft = (params: any = {}) => {
  return request({
    url: `${THREAD_APIS.UPDATE_THREAD_DRAFT}${params.tid}`,
    method: MethodEnum.PUT,
    params,
  }).then((data: ICreateThreadRes) => data);
};

export enum ThreadReplySortEnum {
  asc = 'asc',
  desc = 'desc',
  likesHot = 'likes_hot',
}

/**
 * 获取文章回复（评论区）
 * @param tid 文章 id
 * @param offset 偏移量
 * @param limit 限制返回条数
 * @returns
 */
export const getThreadReply = (tid: number | string, offset: number, limit: number, isServer = false, sort?: string, headers: any = {}) => {
  const baseUrl = isServer ? serverTriggerUrl : '';
  const url = baseUrl + THREAD_APIS.GET_THREAD_REPLAY.replace('{tid}', tid.toString());
  const adminPostToken = typeof headers?.adminPostToken === 'string' ? headers.adminPostToken : '';
  const adminToken = typeof headers?.adminToken === 'string' ? headers.adminToken : '';
  return request({
    url,
    returnRaw: true,
    headers,
    method: MethodEnum.GET,
    params: {
      offset,
      limit,
      sort: sort || ThreadReplySortEnum.likesHot,
      ...(adminPostToken ? { adminPostToken } : {}),
      ...(adminToken ? { adminToken } : {}),
    }
  }).then((res: any) => {
    const payload = res?.data ?? res;

    if (Array.isArray(payload)) {
      return {
        items: payload,
        total_items: payload.length,
        ext: undefined,
      };
    }

    const {
      items = [],
      total_items,
      total,
      ext,
    } = (payload || {}) as {
      items?: ICommentListRes[];
      total_items?: number;
      total?: number;
      ext?: {
        previewPost?: ICommentListRes;
        [key: string]: any;
      };
    };
    const normalizedItems = Array.isArray(items) ? items : [];

    return {
      items: normalizedItems,
      total_items: total_items ?? total ?? normalizedItems.length,
      ext,
    };
  }) as Promise<{
    items: ICommentListRes[];
    total_items: number;
    ext?: {
      previewPost?: ICommentListRes;
      [key: string]: any;
    };
  }>
}

/**
 * 发表新回帖
 * @param tid 文章 id
 * @param message 回帖内容
 * @param parentId 回复回帖时传入，回帖 pid
 * @returns
 */
export const postThreadReply = (tid: string, message: string, parentId?: string | number): Promise<{
  ext: {
    tips: {
      type: string;
      value: number;
      message: string;
    }
  };
  item: ICommentListRes;
}> => {
  const url = THREAD_APIS.POST_THREAD_REPLY.replace('{tid}', tid.toString());
  return request({
    url,
    method: MethodEnum.POST,
    params: {
      message,
      parent_id: parentId,
    }
  });
}

export interface IThreadHotItem {
  tid: number;
  title: string;
  dateline: number;
}
export interface IThreadHotRes {
  items: IThreadHotItem[];
}

export const getThreadHot = () => request({
  url: `${THREAD_APIS.GET_THREAD_HOT}`,
  method: MethodEnum.GET,
}).then((data: IThreadHotRes) => data?.items || []).catch(e => Promise.resolve(e));
